import * as React from 'react';
import {
  copyToClipboard,
  type Response,
} from '@packages/utilities/clipboard-utils';

/**
 * The `useCopyToClipboard` hook API has three pieces
 *
 * `copyToClipboard`: string => void, a function the consumer uses to begin the
 * copying process.
 *
 * `reset`: () => void, a function to manually reset the state of the copying machine
 *
 * `state`: 'idle' | 'pending' | 'success' | 'failure': the current state of the
 * copying machine
 *
 * This API should allow a consumer to copy basically any text they would like
 * to the user's clipboard, while having enough manual control to construct a
 * variety of UIs with the functionality. The consumer can respond to state changes
 * and update the UI to inform the user accordingly.
 */

type States = 'idle' | 'pending' | 'success' | 'failure';
type Actions =
  | {
      type: 'ATTEMPT_COPY';
      payload: string;
    }
  | 'COPY_FAILURE'
  | 'COPY_SUCCESS'
  | 'RESET';

type ActionMap<T> = {
  ATTEMPT_COPY: T;
  COPY_FAILURE: T;
  COPY_SUCCESS: T;
  RESET: T;
};

type PartialActionMap<T> = {
  ATTEMPT_COPY?: T;
  COPY_FAILURE?: T;
  COPY_SUCCESS?: T;
  RESET?: T;
};

const nonResponsiveActions: ActionMap<undefined> = {
  ATTEMPT_COPY: undefined,
  COPY_FAILURE: undefined,
  COPY_SUCCESS: undefined,
  RESET: undefined,
};

const respondsTo = (
  obj: PartialActionMap<States>
): ActionMap<States | undefined> => ({
  ...nonResponsiveActions,
  ...obj,
});

const initialState = {
  current: 'idle',
  data: {
    text: null,
  },
} as const;

const NEXT_STATE_GRAPH = {
  idle: respondsTo({
    ATTEMPT_COPY: 'pending',
  }),
  pending: respondsTo({
    COPY_FAILURE: 'failure',
    COPY_SUCCESS: 'success',
  }),
  success: respondsTo({
    RESET: 'idle',
  }),
  failure: respondsTo({
    RESET: 'idle',
  }),
} as const;

const toActionObject = (
  action:
    | string
    | Actions
    | {
        payload: string;
        type: 'ATTEMPT_COPY';
      }
) => (typeof action === 'string' ? {type: action} : action);

type ReducerState = {
  current: States;
  data: {
    text: string | null;
  };
};

const copyReducer = (state: ReducerState, action: Actions): ReducerState => {
  const actionObject = toActionObject(action);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'ActionMap<States | undefined>'.
  const nextState = NEXT_STATE_GRAPH[state.current][actionObject.type];
  // We only need `text` while in the `pending` state and that is supplied
  // in the `ATTEMPT_COPY` `payload`, so `null` it out every other time.
  // @ts-expect-error - TS2339 - Property 'payload' does not exist on type '{ type: "ATTEMPT_COPY"; payload: string; } | { type: string; }'. | TS2339 - Property 'payload' does not exist on type '{ type: "ATTEMPT_COPY"; payload: string; } | { type: string; }'.
  const nextText = actionObject.payload ? actionObject.payload : null;

  return {
    current: nextState ? nextState : state.current,
    data: {
      text: nextText,
    },
  };
};

type CopyFn = (arg1: string) => Promise<Response>;
type Output = {
  copyToClipboard: (arg1: string) => void;
  reset: () => void;
  state: States;
};

// By creating a dependency injection version of this, we can test out the logic
// of the hook very easily by passing different copyFns to it
export const copyToClipboardHookFactory = (copyFn: CopyFn) => (): Output => {
  const [state, dispatch] = React.useReducer(copyReducer, initialState);
  const {current, data} = state;

  const copy = (text: string) => {
    dispatch({type: 'ATTEMPT_COPY', payload: text});
  };

  React.useEffect(() => {
    if (current === 'pending' && data.text) {
      copyFn(data.text).then(
        () => dispatch('COPY_SUCCESS'),
        () => dispatch('COPY_FAILURE')
      );
    }
  }, [current, data.text]);

  const reset = React.useCallback(() => {
    dispatch('RESET');
  }, [dispatch]);

  return {
    copyToClipboard: copy,
    reset,
    state: state.current,
  };
};

export const useCopyToClipboard = copyToClipboardHookFactory(copyToClipboard);

type WithTooltipArgs = {
  errorContent: React.ReactNode;
  hoverContent: React.ReactNode;
  onComplete: () => void;
  onError: () => void;
  successContent: React.ReactNode;
  value: string;
  mouseEnterDelay?: number;
  resetDelay?: number;
};

export const useCopyToClipboardWithTooltip = ({
  errorContent,
  hoverContent,
  onComplete,
  onError,
  successContent,
  value,
  mouseEnterDelay = 750,
  resetDelay = 2000,
}: WithTooltipArgs) => {
  const {
    copyToClipboard: copy,
    reset,
    state: copyingState,
  } = useCopyToClipboard();
  const [hoverState, setHoverState] = React.useState(false);
  const delayTimeout = React.useRef(null);

  const derivedState = getDerivedState(copyingState, hoverState);

  const handleClick = () => {
    copy(value);
  };

  // Necessary to allow this hook to work with the Spring Tooltip component.
  // For controlled tooltips, we must provide the `isOpen` prop and the `onOpenChange` prop.
  const onOpenChange = (nextOpenState: boolean) => {
    if (nextOpenState) {
      handleMouseEnter();
    } else {
      handleMouseLeave();
    }
  };

  const handleMouseEnter = () => {
    // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'null'.
    delayTimeout.current = setTimeout(() => {
      setHoverState(true);
    }, mouseEnterDelay);
  };

  const handleMouseLeave = () => {
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
      delayTimeout.current = null;
    }

    setHoverState(false);
  };

  React.useEffect(() => {
    // @ts-expect-error - TS7034 - Variable 'timeoutId' implicitly has type 'any' in some locations where its type cannot be determined.
    let timeoutId;

    const delayedReset = () => {
      timeoutId = setTimeout(() => {
        reset();
        setHoverState(false);
      }, resetDelay);
    };

    if (derivedState === 'failure') {
      onError();
      delayedReset();
    }

    if (derivedState === 'success') {
      onComplete();
      delayedReset();
    }

    return () => {
      // @ts-expect-error - TS7005 - Variable 'timeoutId' implicitly has an 'any' type.
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onComplete, onError, reset, derivedState, resetDelay]);

  return {
    copyToClipboard,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    onOpenChange,
    reset,
    state: derivedState,
    tooltipProps: getTooltipProps(
      derivedState,
      hoverContent,
      successContent,
      errorContent
    ),
  };
};

function getDerivedState(copyingState: string | States, hoveredState: boolean) {
  if (hoveredState) {
    if (copyingState === 'idle' || copyingState === 'pending') {
      return 'hovered';
    }
  }

  return copyingState;
}

function getTooltipProps(
  state: string | States,
  hoverContent: React.ReactNode,
  successContent: React.ReactNode,
  errorContent: React.ReactNode
) {
  switch (state) {
    case 'success':
      return {
        // For use with legacy design system ControlledTooltipTrigger
        open: true,
        content: successContent,
        success: true,
        // For use with Spring Tooltip
        isOpen: true,
        label: successContent,
        colorVariant: 'success',
      };

    case 'failure':
      return {
        // For use with legacy design system ControlledTooltipTrigger
        open: true,
        content: errorContent,
        error: true,
        // For use with Spring Tooltip
        isOpen: true,
        label: errorContent,
        colorVariant: 'danger',
      };

    case 'hovered':
      return {
        // For use with legacy design system ControlledTooltipTrigger
        open: Boolean(hoverContent),
        content: hoverContent,
        alternate: true,
        // For use with Spring Tooltip
        isOpen: Boolean(hoverContent),
        label: hoverContent,
        colorVariant: 'default',
      };

    case 'idle':
    case 'pending':
    default:
      return {
        // For use with legacy design system ControlledTooltipTrigger
        open: false,
        content: null,
        // For use with Spring Tooltip
        isOpen: false,
        label: null,
      };
  }
}
